import { MarkerData, Center } from './GoogleMaps.types';

export const releaseWebGLContext = (container: HTMLElement): void => {
  container.querySelectorAll('canvas').forEach((canvas) => {
    const context = canvas.getContext('webgl') || canvas.getContext('webgl2');
    if (context) {
      const loseContextExtension = context.getExtension('WEBGL_lose_context');
      if (loseContextExtension) loseContextExtension.loseContext();
    }
  });
};

export const findNearestPosition = (
  panoramaPosition: google.maps.LatLng,
  positions: { lat: number; lng: number }[],
): { lat: number; lng: number } => {
  let nearestPosition = positions[0];
  let nearestDistance = google.maps.geometry.spherical.computeDistanceBetween(
    panoramaPosition,
    new google.maps.LatLng(nearestPosition.lat, nearestPosition.lng),
  );

  for (let index = 1; index < positions.length; index++) {
    const distance = google.maps.geometry.spherical.computeDistanceBetween(
      panoramaPosition,
      new google.maps.LatLng(positions[index].lat, positions[index].lng),
    );
    if (distance < nearestDistance) {
      nearestPosition = positions[index];
      nearestDistance = distance;
    }
  }

  return nearestPosition;
};

export const orientPanoramaToNearest = (
  panorama: google.maps.StreetViewPanorama,
  markerData: MarkerData[] | undefined,
  center: { lat: number; lng: number },
): void => {
  const panoramaPosition = panorama.getPosition();
  const positions = markerData?.length ? markerData.map((marker) => ({ lat: marker.lat, lng: marker.lng })) : [center];

  if (!panoramaPosition || !positions.length) return;

  const nearestPosition = findNearestPosition(panoramaPosition, positions);
  const heading = google.maps.geometry.spherical.computeHeading(
    panoramaPosition,
    new google.maps.LatLng(nearestPosition.lat, nearestPosition.lng),
  );
  panorama.setPov({ heading, pitch: 10 });
};

const MARKER_ICON_SVG = `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(
  '<svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 28 28">' +
    '<circle cx="14" cy="14" r="13" fill="#2c24c3" stroke="#ffffff" stroke-width="1"/>' +
    '<g transform="translate(6 6) scale(1)">' +
    '<path d="M11 1H5C4.602 1 4.221 1.158 3.939 1.439C3.658 1.721 3.5 2.102 3.5 2.5V12C3.5 12.398 3.658 12.779 3.939 13.061C4.221 13.342 4.602 13.5 5 13.5H11C11.398 13.5 11.779 13.342 12.061 13.061C12.342 12.779 12.5 12.398 12.5 12V2.5C12.5 2.102 12.342 1.721 12.061 1.439C11.779 1.158 11.398 1 11 1ZM11.5 12C11.5 12.133 11.447 12.26 11.354 12.354C11.26 12.447 11.133 12.5 11 12.5H5C4.867 12.5 4.74 12.447 4.646 12.354C4.553 12.26 4.5 12.133 4.5 12V2.5C4.5 2.367 4.553 2.24 4.646 2.146C4.74 2.053 4.867 2 5 2H11C11.133 2 11.26 2.053 11.354 2.146C11.447 2.24 11.5 2.367 11.5 2.5V12Z" fill="#ffffff"/>' +
    '<path d="M10.354 14.957C10.447 14.864 10.5 14.737 10.5 14.604C10.5 14.471 10.447 14.344 10.354 14.25C10.26 14.157 10.133 14.104 10 14.104H6C5.867 14.104 5.74 14.157 5.646 14.25C5.553 14.344 5.5 14.471 5.5 14.604C5.5 14.737 5.553 14.864 5.646 14.957C5.74 15.051 5.867 15.104 6 15.104H10C10.133 15.104 10.26 15.051 10.354 14.957Z" fill="#ffffff"/>' +
    '</g>' +
    '</svg>',
)}`;

const TOOLTIP_ATTR = 'data-street-view-tooltip';

const createTooltipElement = (container: HTMLElement): HTMLDivElement => {
  const existing = container.querySelector(`[${TOOLTIP_ATTR}]`);
  if (existing) existing.remove();

  const tooltip = document.createElement('div');
  tooltip.setAttribute(TOOLTIP_ATTR, '');
  Object.assign(tooltip.style, {
    position: 'absolute',
    zIndex: '1000',
    pointerEvents: 'none',
    display: 'none',
    background: '#1a1a2e',
    color: '#fff',
    fontFamily: "'Inter', sans-serif",
    fontSize: '12px',
    padding: '6px 10px',
    borderRadius: '6px',
    whiteSpace: 'pre-line',
    transform: 'translate(-50%, -100%)',
    marginTop: '-10px',
  });
  container.style.position = 'relative';
  container.appendChild(tooltip);
  return tooltip;
};

export const addMarkersToPanorama = (
  panorama: google.maps.StreetViewPanorama,
  markerData: MarkerData[] | undefined,
  center: { lat: number; lng: number },
  markersRef: React.MutableRefObject<google.maps.Marker[]>,
  container: HTMLElement,
): void => {
  const tooltipElement = createTooltipElement(container);

  const icon = {
    url: MARKER_ICON_SVG,
    scaledSize: new google.maps.Size(28, 28),
    anchor: new google.maps.Point(14, 14),
  };

  const markers = markerData?.length ? markerData : [{ id: 0, ...center }];

  markers.forEach(({ lat, lng, tooltip }) => {
    const marker = new google.maps.Marker({
      position: { lat, lng },
      icon,
    });
    marker.setMap(panorama as unknown as google.maps.Map);

    if (tooltip) {
      marker.addListener('mouseover', (event: google.maps.MapMouseEvent) => {
        tooltipElement.textContent = tooltip;
        tooltipElement.style.display = 'block';
        const domEvent = event.domEvent as MouseEvent;
        const rect = container.getBoundingClientRect();
        tooltipElement.style.left = `${domEvent.clientX - rect.left}px`;
        tooltipElement.style.top = `${domEvent.clientY - rect.top}px`;
      });

      marker.addListener('mouseout', () => {
        tooltipElement.style.display = 'none';
      });
    }

    markersRef.current.push(marker);
  });
};

export const createPanorama = (container: HTMLElement, center: Center): google.maps.StreetViewPanorama =>
  new google.maps.StreetViewPanorama(container, {
    position: center,
    pov: { heading: 0, pitch: 10 },
    enableCloseButton: false,
    addressControl: false,
    linksControl: false,
    panControl: true,
    zoomControl: true,
    fullscreenControl: false,
    visible: true,
  });

export const attachPanoramaListeners = (
  panorama: google.maps.StreetViewPanorama,
  setIsPanoramaReady: (ready: boolean) => void,
  onPositionChangeRef: React.MutableRefObject<((position: Center) => void) | undefined>,
): google.maps.MapsEventListener => {
  google.maps.event.addListenerOnce(panorama, 'status_changed', () => {
    if (panorama.getStatus() === google.maps.StreetViewStatus.OK) {
      setIsPanoramaReady(true);
    }
  });

  return panorama.addListener('position_changed', () => {
    const position = panorama.getPosition();
    if (position && onPositionChangeRef.current) {
      onPositionChangeRef.current({ lat: position.lat(), lng: position.lng() });
    }
  });
};

export const cleanupPanorama = (
  panorama: google.maps.StreetViewPanorama,
  positionListener: google.maps.MapsEventListener,
  markersRef: React.MutableRefObject<google.maps.Marker[]>,
  container: HTMLElement,
): void => {
  google.maps.event.removeListener(positionListener);
  markersRef.current.forEach((marker) => marker.setMap(null));
  markersRef.current = [];
  panorama.setVisible(false);
  releaseWebGLContext(container);
};
